// Card bricks (presentation-2 contract section 5.2): hand-fan, market-row, card-grid, tableau, pile
// and objective-list. Objects draw through faces; hidden contents draw as backs with counts.

import type { BrickInstance } from '../presentation/types';
import type { ObjectView, ZoneView } from '../model/table';
import { useScene, zonesFor, zonesOf, countersFor, seatOf } from '../scene/SceneContext';
import { Piece, PublicCardBack } from './Piece';
import { targetProps, usePlay } from '../play/PlayController';
import { Pips, fieldOf, useOccupants } from './occupants';
import { useEffect, useRef, useState } from 'react';
import { fittedFan } from './fanFit';
import { fittedGridWidth } from './gridFit';
import { ArtImage } from '../art/ArtImage';
import './tableau.css';

interface BrickProps { inst: BrickInstance; seat: string | null }

const WIDTHS = { s: 64, m: 92, l: 124 } as const;
const width = (inst: BrickInstance, fallback: keyof typeof WIDTHS) => (inst.props?.width as number | undefined) ?? WIDTHS[((inst.props?.size as keyof typeof WIDTHS | undefined) ?? fallback)];

function useZone(inst: BrickInstance, seat: string | null): ZoneView | null {
  const { table } = useScene();
  return zonesFor(inst, table, seatOf(inst, seat, table.selfSeat))[0] ?? null;
}

/**
 * Cross-brick link (`linkField` + `linkTo`): a piece names other objects by catalog name through a field,
 * such as a Critter's `pairsWith`; hovering it lights the objects of that name in the `linkTo` zones.
 */
function useLinks(inst: BrickInstance, seat: string | null): (o: ObjectView) => Set<string> | null {
  const { table } = useScene();
  const field = inst.props?.linkField as string | undefined, to = inst.props?.linkTo as string | undefined;
  if (!field || !to) return () => null;
  const candidates = zonesOf(to, table, seat).flatMap(z => z.objects);
  return o => {
    const value = fieldOf(o, field);
    if (typeof value !== 'string' || !value) return null;
    const ids = new Set(candidates.filter(c => c.id !== o.id && (c.catalog?.name === value || c.title === value)).map(c => c.id));
    return ids.size ? ids : null;
  };
}

function hiddenCount(zone: ZoneView): number {
  return zone.count !== null ? Math.max(0, zone.count - zone.objects.reduce((n, o) => n + (o.count ?? 1), 0)) : 0;
}

function Backs({ count, w, label }: { count: number; w: number; label: string }) {
  if (count <= 0) return null;
  return (
    <div className="backs" role="img" aria-label={`${count} hidden ${label}`} style={{ '--piece-w': `${w}px` } as React.CSSProperties}>
      {Array.from({ length: Math.min(count, 5) }, (_, i) => <span key={i} className="back-card" style={{ '--i': i } as React.CSSProperties}><PublicCardBack width={34} /></span>)}
      <span className="backs-count">{count}</span>
    </div>
  );
}

export function ZoneFrame({ zone, label, className, children }: { zone: ZoneView; label?: string; className: string; children: React.ReactNode }) {
  const play = usePlay();
  const anchor = `zone:${zone.id}`;
  const st = play?.stateOf(anchor);
  const t = targetProps(play, anchor, label ?? zone.label, `brick-zone ${className}`);
  const count = zone.count ?? zone.objects.reduce((n, o) => n + (o.count ?? 1), 0);
  return (
    <section {...t} role={st?.selectable ? 'button' : 'region'} tabIndex={st?.selectable ? 0 : -1} aria-label={`${label ?? zone.label}, ${count}`} data-motion-zone={zone.id}>
      <h3 className="brick-title">{label ?? zone.label}<span className="brick-count">{count}</span></h3>
      {children}
    </section>
  );
}

export function HandFan({ inst, seat }: BrickProps) {
  const zone = useZone(inst, seat);
  const { table } = useScene();
  const linksOf = useLinks(inst, seatOf(inst, seat, table.selfSeat));
  const fit = !!inst.props?.fit;
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [room, setRoom] = useState<number | null>(null);
  const [sortedIds, setSortedIds] = useState<string[]>([]);
  const dragId = useRef<string | null>(null);
  const dragEndedAt = useRef(0);
  useEffect(() => {
    const el = rowRef.current;
    if (!fit || !el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const style = getComputedStyle(el);
      setRoom(Math.max(0, el.clientWidth - parseFloat(style.paddingLeft || '0') - parseFloat(style.paddingRight || '0')));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, [fit, zone?.id]);
  if (!zone) return null;
  const w = width(inst, 'm');
  const stacked = inst.props?.groupBy === 'catalog' || zone.objects.some(o => (o.count ?? 1) > 1);
  const spread = (inst.props?.maxSpread as number | undefined) ?? 12;
  // Objects whose `stack` names another object in this hand tuck under it (a weapon under its hero).
  const inZone = new Set(zone.objects.map(o => o.id));
  const naturalHosts = zone.objects.filter(o => !o.stack || !inZone.has(o.stack));
  const sortable = inst.props?.sortable === true;
  const hosts = sortable ? [...naturalHosts].sort((a, b) => {
    const ai = sortedIds.indexOf(a.id), bi = sortedIds.indexOf(b.id);
    return (ai < 0 ? naturalHosts.indexOf(a) + sortedIds.length : ai) - (bi < 0 ? naturalHosts.indexOf(b) + sortedIds.length : bi);
  }) : naturalHosts;
  const move = (from: string, to: string) => {
    if (from === to) return;
    const next = hosts.map(o => o.id);
    const source = next.indexOf(from), destination = next.indexOf(to);
    if (source < 0 || destination < 0) return;
    next.splice(destination, 0, next.splice(source, 1)[0]!);
    setSortedIds(next);
  };
  const tucked = (host: string) => zone.objects.filter(o => o.stack === host);
  const n = hosts.length;
  // The share of a card its neighbour may cover; a cap keeps every title band readable.
  const maxOverlap = (inst.props?.maxOverlap as number | undefined) ?? 0.6;
  // `fit`: one row that overlaps only when the cards do not fit side by side (gap 8 px).
  const fitted = fittedFan(n, w, room, maxOverlap);
  const overlap = fit ? fitted.overlap : Math.min(maxOverlap, n > 5 ? 0.25 + (n - 5) * 0.08 : 0.25);
  const overlapStyle = fit ? ({ '--fan-overlap': overlap > 0 ? -overlap - 8 / w : 0 } as React.CSSProperties)
    : !stacked && (n > 5 || overlap < 0.25) ? ({ '--fan-overlap': -overlap } as React.CSSProperties) : undefined;
  // `peek` (px): only the top of each card shows, so a tall hand costs a short strip; hovering a card (`hoverDetails`) shows all of it.
  const peek = typeof inst.props?.peek === 'number' ? inst.props.peek : null;
  return (
    <ZoneFrame zone={zone} label={inst.props?.label as string | undefined} className={`hand-fan${peek ? ' has-peek' : ''}${stacked ? ' is-stacked' : ''}${fit ? ' is-fit' : ''}${fit && !stacked && fitted.wrap ? ' is-overflowing' : ''}${inst.props?.align === 'center' ? ' is-centered' : ''}`}>
      <div ref={rowRef} className="hand-items" style={peek ? { ...overlapStyle, maxHeight: peek, overflow: 'hidden' } : overlapStyle}>
        {hosts.map((o, i) => {
          const angle = !stacked && hosts.length > 1 ? -spread / 2 + (spread * i) / (hosts.length - 1) : 0;
          const piece = (
            <Piece key={o.id} object={o} zoneKind={zone.kind} width={w} className={stacked ? 'is-stack' : ''} links={linksOf(o)} hoverDetails={inst.props?.hoverDetails === true} inspectOnHover={inst.props?.inspectOnHover === true} previewPlacement={inst.props?.previewPlacement === 'right' ? 'right' : undefined}
              style={stacked ? { '--stack': Math.min(3, (o.count ?? 1) - 1) } as React.CSSProperties : { transform: `rotate(${angle}deg)` }}
              badge={(o.count ?? 1) > 1 ? <span className="piece-count" aria-hidden="true">{o.count}</span> : undefined} />
          );
          const under = tucked(o.id);
          const rendered = !under.length ? piece : (
            // Attachments (an equipped weapon) tuck under their host and stay their own targets.
            <div key={o.id} className="fan-host" style={{ '--piece-w': `${w}px` } as React.CSSProperties}>
              {under.map((t, j) => <Piece key={t.id} object={t} zoneKind={zone.kind} width={w * 0.82} className="is-tucked" hoverDetails={inst.props?.hoverDetails === true} inspectOnHover={inst.props?.inspectOnHover === true} previewPlacement={inst.props?.previewPlacement === 'right' ? 'right' : undefined} style={{ '--tuck': j } as React.CSSProperties} />)}
              {piece}
            </div>
          );
          if (!sortable) return rendered;
          return (
            <div key={o.id} className="fan-order" onDragOver={event => event.preventDefault()} onDrop={event => { event.preventDefault(); if (dragId.current) move(dragId.current, o.id); dragId.current = null; }}>
              {rendered}
              <button type="button" className="fan-sort-handle" draggable aria-label={`Reorder ${o.title}`} title="Drag to reorder, click to move right, or use Alt+Left/Right"
                onDragStart={event => { dragId.current = o.id; event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', o.id); }} onDragEnd={() => { dragId.current = null; dragEndedAt.current = Date.now(); }}
                onClick={() => { if (Date.now() - dragEndedAt.current < 250) return; const other = hosts[(i + 1) % hosts.length]; if (other) move(o.id, other.id); }}
                onKeyDown={event => { if (!event.altKey || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return; event.preventDefault(); const other = hosts[i + (event.key === 'ArrowLeft' ? -1 : 1)]; if (other) move(o.id, other.id); }}>⇄</button>
            </div>
          );
        })}
        <Backs count={hiddenCount(zone)} w={w * 0.7} label="cards" />
        {zone.objects.length === 0 && hiddenCount(zone) === 0 && <p className="brick-empty">Empty</p>}
      </div>
    </ZoneFrame>
  );
}

export function MarketRow({ inst, seat }: BrickProps) {
  const zone = useZone(inst, seat);
  const play = usePlay();
  if (!zone) return null;
  const w = width(inst, 'm');
  const slots = (inst.props?.slots as number | undefined) ?? zone.objects.length;
  const column = inst.props?.orientation === 'column';
  // `depth`: each slot sits a shade darker than the one before (a dungeon hall's ranks); `slotLabel`
  // names each slot ("Rank 1"). Seeded by Thunderstone.
  const depth = !!inst.props?.depth;
  const slotLabel = inst.props?.slotLabel as string | undefined;
  const n = Math.max(slots, zone.objects.length);
  const wrap = (i: number, node: React.ReactNode, key: string) => (depth || slotLabel ? (
    <div key={key} className="market-slot" style={depth ? ({ '--depth': n > 1 ? i / (n - 1) : 0 } as React.CSSProperties) : undefined}>
      {slotLabel && <p className="market-slot-label">{slotLabel} {i + 1}</p>}
      {node}
    </div>
  ) : node);
  return (
    <ZoneFrame zone={zone} label={inst.props?.label as string | undefined} className={`market-row${column ? ' is-column' : ''}${depth ? ' is-depth' : ''}`}>
      <div className="market-items">
        {Array.from({ length: n }, (_, i) => {
          const o = zone.objects[i];
          if (o) return wrap(i, <Piece key={o.id} object={o} zoneKind={zone.kind} width={w} />, o.id);
          const anchor = `slot:${zone.id}#${i}`;
          const t = targetProps(play, anchor, `Empty slot ${i + 1}`, 'market-empty');
          return wrap(i, <div key={`empty-${i}`} {...t} role="img" tabIndex={-1} style={{ '--piece-w': `${w}px` } as React.CSSProperties} />, `empty-${i}`);
        })}
      </div>
    </ZoneFrame>
  );
}

/** The zones with only the objects of the given ui-2 kinds (all objects without `kinds`); a filtered zone has no hidden-card count. */
export function onlyKinds(zones: ZoneView[], kinds: readonly string[] | undefined): ZoneView[] {
  return kinds ? zones.map(z => ({ ...z, count: null, objects: z.objects.filter(o => kinds.includes(o.kind)) })) : zones;
}

export function CardGrid({ inst, seat }: BrickProps) {
  const { table } = useScene();
  const play = usePlay();
  const own = seatOf(inst, seat, table.selfSeat);
  // `kinds`: draw only objects of these ui-2 kinds (a zone mixing a summary marker with dice, say, can be laid out as two bricks).
  const zones = onlyKinds(zonesFor(inst, table, own), inst.props?.kinds as string[] | undefined);
  const occupantsOf = useOccupants(inst, own);
  const linksOf = useLinks(inst, own);
  const columns = inst.props?.columns as number | undefined;
  const fit = !!inst.props?.fit;
  const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
  const mountedGrids = useRef(new Map<string, HTMLDivElement>());
  const gridCallbacks = useRef(new Map<string, (element: HTMLDivElement | null) => void>());
  const gridRefFor = (key: string) => {
    let callback = gridCallbacks.current.get(key);
    if (!callback) {
      callback = element => {
        if (element) mountedGrids.current.set(key, element);
        else mountedGrids.current.delete(key);
        // Preserve the existing shared fitted width, observing a mounted grid
        // even when another selected zone removes its own element.
        setGridElement([...mountedGrids.current.values()].at(-1) ?? null);
      };
      gridCallbacks.current.set(key, callback);
    }
    return callback;
  };
  const [gridRoom, setGridRoom] = useState<{width: number; gap: number} | null>(null);
  useEffect(() => {
    const el = gridElement;
    if (!fit || !columns || !el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      // An empty zone can remove this grid, and a tab can temporarily hide it.
      // Neither is a one-pixel layout; retain the last usable measurement.
      if (!el.isConnected || el.clientWidth <= 0) return;
      const style = getComputedStyle(el);
      setGridRoom({width: Math.max(0, el.clientWidth - parseFloat(style.paddingLeft || '0') - parseFloat(style.paddingRight || '0')), gap: parseFloat(style.columnGap || '0') || 0});
    };
    const observer = new ResizeObserver(measure);
    observer.observe(el); measure();
    return () => observer.disconnect();
  }, [fit, columns, gridElement]);
  const w = fit ? fittedGridWidth(columns ?? 0, width(inst, 'm'), gridRoom?.width ?? null, gridRoom?.gap ?? 8) : width(inst, 'm');
  const slots = inst.props?.slots as number | undefined;
  const status = !!inst.props?.status;
  const { seatLabel } = useScene();
  // `piles`: every selected zone is a supply pile, drawn as one cell of a single grid (its top object
  // with the pile's count) instead of a titled section each. Seeded by Thunderstone's village supply.
  if (inst.props?.piles) {
    const label = (inst.props?.label as string | undefined) ?? 'Piles';
    const total = zones.reduce((n, z) => n + (z.count ?? z.objects.length), 0);
    return (
      <section className="brick-zone card-grid is-piles" aria-label={`${label}, ${zones.length} piles`}>
        <h3 className="brick-title">{label}<span className="brick-count">{total}</span></h3>
        <div ref={gridRefFor('piles')} className="grid-items" style={columns ? { gridTemplateColumns: `repeat(${columns}, minmax(0, ${w}px))` } : undefined}>
          {zones.map(zone => {
            const inZone = new Set(zone.objects.map(o => o.id));
            const top = zone.objects.find(o => !o.stack || !inZone.has(o.stack)) ?? null;
            const count = zone.count ?? zone.objects.length;
            return (
              <div key={zone.id} className={`pile-cell${count === 0 ? ' is-empty' : ''}`} data-motion-zone={zone.id} style={{ '--piece-w': `${w}px` } as React.CSSProperties}>
                {top
                  ? <Piece object={top} zoneKind={zone.kind} width={w} hoverDetails={inst.props?.hoverDetails === true} inspectOnHover={inst.props?.inspectOnHover === true} badge={<span className="piece-count" aria-label={`${count} left`}>{count}</span>} />
                  : <div className="pile-cell-empty" role="img" aria-label={`${zone.label}: empty`}>Empty</div>}
                <p className="pile-cell-label">{zone.label}</p>
              </div>
            );
          })}
          {zones.length === 0 && <p className="brick-empty">None</p>}
        </div>
      </section>
    );
  }
  // A status line under each object: the move it offers now, else who holds it, else open.
  const statusOf = (o: ObjectView): { text: string; tone: string } => {
    const st = play?.stateOf(`obj:${o.id}`);
    if (st?.selectable) return { text: st.tip ? `${st.tip}: now` : 'Available now', tone: 'is-now' };
    if (st?.option) return { text: st.tip ?? 'Choose this', tone: 'is-now' };
    const holder = o.owner ?? (typeof o.fields.claimedBy === 'string' ? o.fields.claimedBy : null);
    if (holder) return { text: holder === table.selfSeat ? 'Yours' : `Taken by ${seatLabel(holder)}`, tone: 'is-taken' };
    return { text: 'Open', tone: 'is-open' };
  };
  return (
    <>
      {zones.filter(z => inst.props?.showEmpty !== false || z.objects.length > 0 || hiddenCount(z) > 0).map(zone => {
          // A cross-zone attachment is still a visible object in this zone. Only
          // hide attachments whose host is rendered in the same zone.
          const inZone = new Set(zone.objects.map(o => o.id));
          const shown = zone.objects.filter(o => !o.stack || !inZone.has(o.stack));
        // A fixed number of slots keeps empty places visible (a market that refills).
        const empty = slots ? Math.max(0, slots - shown.length) : 0;
        return (
          <ZoneFrame key={zone.id} zone={zone} label={zones.length === 1 ? inst.props?.label as string | undefined : undefined} className={`card-grid${inst.props?.heading ? " has-heading" : ""}`}>
            <div ref={gridRefFor(`zone:${zone.id}`)} className="grid-items" style={columns ? { gridTemplateColumns: `repeat(${columns}, minmax(0, ${w}px))` } : undefined}>
              {shown.map(o => {
                const occupants = occupantsOf(o.id);
                const piece = <Piece key={inst.props?.still ? `${o.id}|${o.label}` : o.id} still={inst.props?.still === true} object={o} zoneKind={zone.kind} width={w} links={linksOf(o)} hoverDetails={inst.props?.hoverDetails === true} inspectOnHover={inst.props?.inspectOnHover === true} badge={occupants.length ? <Pips occupants={occupants} capacity={0} /> : undefined} />;
                if (!status) return piece;
                const s = statusOf(o);
                return <div key={o.id} className="grid-cell">{piece}<p className={`grid-status ${s.tone}`}>{s.text}</p></div>;
              })}
              {Array.from({ length: empty }, (_, i) => {
                const t = targetProps(play, `slot:${zone.id}#${shown.length + i}`, `Empty slot ${shown.length + i + 1}`, 'grid-empty');
                return <div key={`empty-${i}`} {...t} role="img" tabIndex={-1} style={{ '--piece-w': `${w}px` } as React.CSSProperties} />;
              })}
              <Backs count={hiddenCount(zone)} w={w * 0.7} label="cards" />
              {zone.objects.length === 0 && hiddenCount(zone) === 0 && !slots && <p className="brick-empty">Empty</p>}
            </div>
          </ZoneFrame>
        );
      })}
    </>
  );
}

/**
 * A player's built area: objects sorted into groups (`groupBy` a field path, in `groupOrder`),
 * laid out in `columns` with dashed empty places up to `capacity`. Objects whose `stack` names
 * another object in the zone tuck under it; occupants from `occupantsFrom` sit on it as pips, followed by
 * `openField` dashed pips for the worker spaces still open on it (a Destination card in a city).
 */
export function tableauLayout(objects: readonly ObjectView[], capacity: number, occupancyField?: string, pairField?: string): {
  groups: { objects: ObjectView[]; occupancy: number }[]; occupied: number; empty: number;
} {
  // Attachment semantics remain unchanged: only root objects contribute capacity.
  const roots = objects.filter(o => !o.stack);
  const byId = new Map(roots.map(o => [o.id, o]));
  const used = new Set<string>();
  const contribution = (o: ObjectView) => {
    const value = occupancyField ? fieldOf(o, occupancyField) : undefined;
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 1;
  };
  const groups: { objects: ObjectView[]; occupancy: number }[] = [];
  let occupied = 0;
  for (const object of roots) {
    if (used.has(object.id)) continue;
    const members = [object];
    const reference = pairField ? fieldOf(object, pairField) : undefined;
    const mate = typeof reference === 'string' ? byId.get(reference) : undefined;
    if (mate && mate !== object && !used.has(mate.id) && !object.faceDown && !mate.faceDown
      && mate.zoneId === object.zoneId && fieldOf(mate, pairField!) === object.id) members.push(mate);
    const occupancy = members.reduce((sum, member) => sum + contribution(member), 0);
    groups.push({ objects: members, occupancy });
    occupied += occupancy;
    for (const member of members) used.add(member.id);
  }
  // Fractional contributions may leave only part of a place; draw whole free places.
  return { groups, occupied, empty: Math.max(0, Math.floor(capacity - occupied)) };
}

export function Tableau({ inst, seat }: BrickProps) {
  const { table, presentation } = useScene();
  const own = seatOf(inst, seat, table.selfSeat);
  const zone = zonesFor(inst, table, own)[0] ?? null;
  const occupantsOf = useOccupants(inst, own);
  if (!zone) return null;
  const w = width(inst, 's');
  const key = inst.props?.groupBy as string | undefined;
  const order = (inst.props?.groupOrder as string[] | undefined) ?? [];
  const map = inst.props?.groupMap ? presentation.theme.maps?.[inst.props.groupMap as string] : undefined;
  const groupOf = (o: ObjectView) => (key ? String(fieldOf(o, key) ?? '') : '');
  const rank = (g: string) => { const i = order.indexOf(g); return i < 0 ? order.length : i; };
  const capacity = (inst.props?.capacity as number | undefined) ?? 0;
  const occupancyField = inst.props?.occupancyField as string | undefined;
  const pairField = inst.props?.pairField as string | undefined;
  // Form indivisible pair groups before category ordering. The first root in
  // the zone's original order is the deterministic category anchor of its pair.
  const layout = tableauLayout(zone.objects, capacity, occupancyField, pairField);
  const groups = layout.groups.map((group, i) => ({ group, i, g: groupOf(group.objects[0]!) }))
    .sort((p, q) => rank(p.g) - rank(q.g) || p.i - q.i).map(entry => entry.group);
  const columns = inst.props?.columns as number | undefined;
  const tucked = (host: string) => zone.objects.filter(o => o.stack === host);
  const openField = inst.props?.openField as string | undefined;
  const openOf = (o: ObjectView) => { const v = openField ? Number(fieldOf(o, openField)) : NaN; return Number.isFinite(v) && v > 0 ? Math.min(6, v) : 0; };
  return (
    <ZoneFrame zone={zone} label={inst.props?.label as string | undefined} className={`tableau${columns ? ' is-grid' : ''}${pairField ? ' with-pairs' : ''}`}>
      {occupancyField && capacity > 0 && <p className="tableau-capacity">{layout.occupied}/{capacity} spaces used · {layout.empty} free</p>}
      <div className="tableau-items" style={columns ? { gridTemplateColumns: `repeat(${columns}, minmax(0, ${inst.props?.width ? `${w}px` : '1fr'}))` } : undefined}>
        {groups.map((group, n) => {
          const draw = (o: ObjectView, member: number) => {
            const g = groupOf(o);
            const occupants = occupantsOf(o.id);
            const open = openOf(o);
            const previous = member > 0 ? group.objects[member - 1] : groups[n - 1]?.objects.at(-1);
            const first = !previous || groupOf(previous) !== g;
            return (
              <div key={o.id} className={`tableau-slot${first ? ' is-group-start' : ''}`} data-group={g || undefined}
                style={map?.[g] ? ({ '--group-color': map[g] } as React.CSSProperties) : undefined}>
                {tucked(o.id).map(t => <Piece key={t.id} object={t} zoneKind={zone.kind} width={w * 0.8} className="is-tucked" hoverDetails={inst.props?.hoverDetails === true} />)}
                <Piece object={o} zoneKind={zone.kind} width={w} hoverDetails={inst.props?.hoverDetails === true} badge={occupants.length || open ? <Pips occupants={occupants} capacity={occupants.length + open} /> : undefined} />
              </div>
            );
          };
          if (group.objects.length === 1) return draw(group.objects[0]!, 0);
          return <div key={group.objects[0]!.id} className="tableau-pair" role="group" aria-label="Paired cards"
            style={{ '--tableau-card-w': `${w}px`, ...(columns ? { gridColumn: `span ${Math.min(2, columns)}` } : {}) } as React.CSSProperties}>
            {group.objects.map(draw)}
            <span className="tableau-pair-caption">{group.occupancy === 1 ? 'Shared space' : 'Paired cards'}</span>
          </div>;
        })}
        {Array.from({ length: layout.empty }, (_, i) => <span key={`e${i}`} className="tableau-empty" aria-hidden="true" />)}
        {groups.length === 0 && capacity === 0 && hiddenCount(zone) === 0 && <p className="brick-empty">No cards yet.</p>}
      </div>
    </ZoneFrame>
  );
}

export function Pile({ inst, seat }: BrickProps) {
  const { table } = useScene();
  const play = usePlay();
  const own = seatOf(inst, seat, table.selfSeat);
  const zone = zonesFor(inst, table, own)[0] ?? null;
  const counter = zone ? null : countersFor(inst, table, own)[0] ?? null;
  if (!zone && !counter) return null;
  const label = (inst.props?.label as string | undefined) ?? zone?.label ?? counter!.label;
  const count = zone ? zone.count ?? zone.objects.reduce((n, o) => n + (o.count ?? 1), 0) : Number(counter!.value);
  const anchor = zone ? `pile:${zone.id}` : `pile:counter:${counter!.id}`;
  const t = targetProps(play, anchor, `${label}, ${count}`, `pile pile-${(inst.props?.tone as string | undefined) ?? 'dark'} pile-${(inst.props?.pattern as string | undefined) ?? 'stripes'}`);
  const st = play?.stateOf(anchor);
  const top = inst.props?.face === 'top' && zone ? zone.objects[zone.objects.length - 1] : undefined;
  return (
    <div {...t} {...(zone ? { 'data-motion-zone': zone.id } : { 'data-motion-counter': counter!.id })}>
      <div className="pile-stack" aria-hidden="true">
          {Array.from({ length: Math.min(4, Math.max(1, Math.ceil(count / 12))) }, (_, i) => <span key={i} className="pile-card" style={{ '--i': i } as React.CSSProperties}>{zone?.visibility === 'hidden' && <><PublicCardBack width={64} />{typeof inst.props?.backArt==='string'&&count>0&&<ArtImage source={inst.props.backArt}/>}</>}</span>)}
        {count === 0 && <span className="pile-empty" />}
      </div>
      {top && <div className="pile-top"><Piece object={top} zoneKind={zone!.kind} width={64} /></div>}
      <span className="pile-label">{label}</span>
      <span className="pile-count">{count}</span>
      {st?.button && <span className="pile-button" aria-hidden="true">{st.button}</span>}
    </div>
  );
}

export function ObjectiveList({ inst, seat }: BrickProps) {
  const { table, board } = useScene();
  const play = usePlay();
  const zone = useZone(inst, seat);
  if (!zone) return null;
  if (inst.props?.showEmpty === false && zone.objects.length === 0 && hiddenCount(zone) === 0) return null;
  const fields = (inst.props?.highlight as string[] | undefined) ?? [];
  const named = inst.props?.board ? board(inst.props.board as string) : null;
  const nodesFor = (o: ObjectView): Set<string> | null => {
    if (!named || fields.length === 0) return null;
    const ids = new Set<string>();
    for (const f of fields) {
      const value = f.startsWith('fields.') ? o.fields[f.slice(7)] ?? o.catalog?.fields?.[f.slice(7)] : undefined;
      if (value === undefined) continue;
      const node = named.board.nodes.find(n => n.id === String(value) || n.id.endsWith(':' + String(value)));
      if (node) ids.add(node.id);
    }
    return ids.size ? ids : null;
  };
  const prompt = inst.props?.prompt as string | undefined;
  return (
    <ZoneFrame zone={zone} label={inst.props?.label as string | undefined} className={`objective-list${zone.objects.length && prompt ? ' has-prompt' : ''}`}>
      {prompt && zone.objects.length > 0 && <p className="objective-prompt">{prompt}</p>}
      <div className="objective-items">
        {zone.objects.map(o => {
          const nodes = nodesFor(o);
          const done = o.fields.completed === true;
          return (
            <div key={o.id} className={`objective${done ? ' is-done' : ''}`}
              onPointerEnter={() => nodes && play?.setHighlight(nodes)} onPointerDown={event => { if (event.pointerType === 'touch' && nodes) play?.setHighlight(nodes); }} onPointerLeave={event => { if (event.pointerType !== 'touch' && nodes) play?.setHighlight(null); }}
              onFocus={() => nodes && play?.setHighlight(nodes)} onBlur={() => nodes && play?.setHighlight(null)}>
              <Piece object={o} zoneKind={zone.kind} width={196} className="is-objective" hoverDetails={inst.props?.hoverDetails === true} />
            </div>
          );
        })}
        <Backs count={hiddenCount(zone)} w={60} label={zone.label.toLowerCase()} />
        {zone.objects.length === 0 && hiddenCount(zone) === 0 && <p className="brick-empty">None</p>}
      </div>
      {table.finished && null}
    </ZoneFrame>
  );
}
